import React from 'react';
import { Link } from 'react-router-dom';
import { buildSides } from '../utils/battleSides';

/**
 * Format seconds into MM:SS or H:MM:SS
 */
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Format view count: 125000 -> "125K", 1200000 -> "1.2M"
 */
const formatViewCount = (count) => {
  if (!count && count !== 0) return null;
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1).replace(/\.0$/, '')}K`;
  }
  return String(count);
};

const VideoCard = ({ video }) => {
  const artists = video.artists || [];
  const sides = buildSides(artists);
  const sideName = (side) =>
    side.artists.map((a) => a.display_name || a.username).join(' & ');
  const duration = formatDuration(video.duration_seconds);
  const views = formatViewCount(video.view_count);

  // Derive gate flags from the API fields when available, falling back to legacy
  // `free` + `price_cents` for backward compatibility with older payloads.
  // The backend now sends `requires_purchase` and `requires_login` directly.
  const requiresPurchase =
    video.requires_purchase !== undefined
      ? video.requires_purchase
      : !!(video.price_cents && video.price_cents >= 50 && !video.free);
  const isFreeExclusive = video.status === 'exclusive' && !requiresPurchase;

  return (
    <Link
      to={`/video/${video.id}`}
      className="video-card group"
      data-testid={`video-card-${video.id}`}
    >
      {/* Thumbnail with cinematic overlay */}
      <div className="relative aspect-video overflow-hidden bg-black" data-testid="video-thumbnail">
        {video.thumbnail_url ? (
          <img
            src={video.thumbnail_url}
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center bg-battle-dark"
            data-testid="video-thumbnail-placeholder"
          >
            <svg className="w-16 h-16 text-gray-800" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
            </svg>
          </div>
        )}

        {/* Deep cinematic gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.45) 45%, rgba(0,0,0,0.05) 75%, transparent 100%)',
          }}
        />

        {/* Play button overlay on hover */}
        <div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
          data-testid="play-overlay"
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-sm
                       scale-75 group-hover:scale-100 transition-transform duration-300"
            style={{
              background: 'rgba(245,230,66,0.92)',
              boxShadow: '0 0 40px rgba(245,230,66,0.55), 0 0 80px rgba(245,230,66,0.2)',
            }}
          >
            <svg className="w-6 h-6 text-battle-dark ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>

        {/* Duration badge - bottom right */}
        {duration && (
          <span
            className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/85 text-white text-xs font-mono rounded"
            data-testid="duration-badge"
          >
            {duration}
          </span>
        )}

        {/* Source badge: YouTube (top right) or Exclusive (top left, premium gradient) */}
        {video.platform === 'youtube' ? (
          <span
            className="badge-youtube absolute top-2 right-2"
            data-testid="source-badge"
          >
            YouTube
          </span>
        ) : (
          <span
            className="badge-exclusive absolute top-2 left-2"
            data-testid="source-badge"
          >
            Exclusive
          </span>
        )}

        {/* Price badge - shown for paid videos */}
        {requiresPurchase && video.price && (
          <span
            className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/85 text-brand-yellow text-xs font-bold rounded flex items-center gap-1"
            data-testid="price-badge"
          >
            {video.price}
          </span>
        )}

        {/* Free-exclusive badge - exclusive + no price: must sign in, no purchase */}
        {isFreeExclusive && (
          <span
            className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/85 text-green-400 text-xs font-bold rounded"
            data-testid="free-exclusive-badge"
          >
            Free &middot; Sign in to watch
          </span>
        )}
      </div>

      {/* Card content */}
      <div className="p-3.5">
        {artists.length >= 2 ? (
          <>
            {/* VS treatment - sides in display font (1v1 = one artist per side, 2v2 = two) */}
            <div className="flex items-center gap-2 mb-1 min-w-0">
              <span className="text-white font-black text-sm truncate font-display leading-tight">
                {sideName(sides[0])}
              </span>
              <span
                className="text-brand-yellow font-black text-[10px] shrink-0 px-1.5 py-0.5 rounded"
                style={{
                  border: '1px solid rgba(245,230,66,0.35)',
                  background: 'rgba(245,230,66,0.10)',
                  letterSpacing: '0.05em',
                }}
              >
                VS
              </span>
              <span className="text-white font-black text-sm truncate font-display leading-tight">
                {sideName(sides[1])}
              </span>
              {/* 2v2 format badge -- explicit designation from the admin,
                  distinct from the VS treatment above (which renders for
                  ANY 2+ artist video, battle-designated or not). */}
              {video.battle_format === '2v2' && (
                <span
                  className="shrink-0 px-1.5 py-0.5 bg-army/20 text-army-light text-[10px] font-black rounded border border-army/40"
                  data-testid="battle-format-badge"
                >
                  2v2
                </span>
              )}
            </div>
            <p className="text-gray-600 text-xs line-clamp-1 mb-2 leading-relaxed">
              {video.title}
            </p>
          </>
        ) : (
          <h3 className="text-white font-bold text-sm mb-2 line-clamp-2 group-hover:text-brand-yellow transition-colors leading-snug">
            {video.title}
          </h3>
        )}

        {/* Meta row: event name + view count */}
        <div className="flex items-center justify-between text-gray-600 text-xs">
          <span className="truncate pr-2">{video.event_name || 'Battle Rap'}</span>
          {views && (
            <span data-testid="view-count" className="flex items-center gap-1 shrink-0 text-gray-500">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {views}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default VideoCard;
